// Effective syllabus content for ONE section — the single place the browser
// resolves the three content layers.
//
// Why this exists: the precedence rule is `section override ?? composed ??
// template`, and it used to be written by hand in two different places inside
// CourseSyllabusPage — once for the on-screen view and once, differently, for
// the PDF. Two hand-written copies of one rule is how the screen and the
// downloaded document came to disagree for the same section. The override half
// now comes from `applyContentOverrideMap`, the same path→field table the
// server uses to project overrides and to write the template on approval, so a
// new override path cannot reach a renderer without going through it.
//
// What stays here rather than in the shared table: the composed layer names two
// of the same fields differently (`courseObjectives` vs `objectives.course`),
// so that translation is inherent to the layer, not duplicated logic. It is
// written once, below.

import {
  applyContentOverrideMap,
  isSyllabusContentOverridePath,
  type ContentBearingTemplate,
  type SyllabusContentOverridePath,
} from '@dosfilos/application/composition';

/** The part of the composed syllabus that overlaps the overridable content.
 *  Both the in-page renderer and the PDF shape their composed object with at
 *  least these two fields. */
export interface ComposedContentLayer {
  description?: Record<string, unknown>;
  courseObjectives: { id: string; code?: string; text: string }[];
}

export interface EffectiveSyllabus<T, C> {
  /** Template with the section's overrides projected on top. */
  template: T;
  /** Composed layer, override-aware: a consumer that prefers `composed` for a
   *  field still sees the section's edit instead of the stale composed value. */
  composed: C | undefined;
  /** Which whitelisted paths this section actually overrode. Lets a caller ask
   *  "did the teacher edit this?" without re-deriving it from field values —
   *  which matters because an override to an EMPTY value is still an override. */
  overridden: ReadonlySet<SyllabusContentOverridePath>;
}

export function resolveEffectiveSyllabus<
  T extends ContentBearingTemplate,
  C extends ComposedContentLayer,
>(args: {
  template: T;
  composed: C | null | undefined;
  overrides: Record<string, unknown> | undefined;
}): EffectiveSyllabus<T, C> {
  const { overrides } = args;
  const overridden = new Set<SyllabusContentOverridePath>(
    Object.keys(overrides ?? {}).filter(isSyllabusContentOverridePath),
  );

  // Override layer, via the shared table.
  const template = applyContentOverrideMap(args.template, overrides);

  // Same overrides, expressed in the composed layer's own field names.
  let composed = args.composed ?? undefined;
  if (composed && overrides) {
    if (overridden.has('description')) {
      composed = { ...composed, description: overrides['description'] as C['description'] };
    }
    if (overridden.has('objectives.course')) {
      composed = {
        ...composed,
        courseObjectives: overrides['objectives.course'] as C['courseObjectives'],
      };
    }
  }

  return { template, composed, overridden };
}
